/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import introJs from "intro.js";

/** MUI imports */
import { Stack } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useNavigate } from "react-router-dom";
import dashboardService from "../../services/dashboard";
import conversionsService from "../../services/conversion";
import accountsService from "../../services/accounts";
import LightTooltip from "../ui/LightTooltip";
import CheckIcon from "@mui/icons-material/Check";

/** React-Redux */
import { useDispatch, useSelector } from "react-redux";
import { addTutorial } from "../../redux/userSlice";
import BaseButton from "../ui/BaseButton";

const DashboardHeader = (props) => {
    const [t] = useTranslation("global");

    const {
        type,
        setXeroView,
        xeroView,
        franchise,
        company,
        setShowSkeleton,
        setDashboardInfo,
        grupal,
        history,
        setHistory,
        setConversions,
        setUsersFilters,
        setAccounts,
    } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const todos = useSelector((state) => state.value);

    /* Component States */

    const handleNavigation = (type) => {
        switch (type) {
            case 1:
                navigate("/accounts");
                break;
            default:
                break;
        }
    };

    const handleClickHistory = async () => {
        setShowSkeleton(true);
        if (!xeroView) {
            await conversionsService
                .getConversionsByFranchise({ franchise_id: franchise ?? undefined, grouped: todos.isGrouped ?? false })
                .then((response) => {
                    if (response.length > 0) {
                        setConversions(response);
                    } else {
                        setConversions([]);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });

            await conversionsService
                .getConversionUsersByFranchise({
                    franchise_id: franchise ?? undefined,
                    grouped: todos.isGrouped ?? false,
                })
                .then((response) => {
                    setUsersFilters(response);
                })
                .catch((err) => {
                    console.log(err);
                });

            await accountsService
                .getAccountsByFranchise({ franchise_id: franchise ?? undefined, grouped: todos.isGrouped ?? false })
                .then((response) => {
                    const newAcc = response.setup.map((item) => {
                        return {
                            id: item._id,
                            name: item.account_name,
                            bankName: item.bank_name,
                        };
                    });
                    setAccounts(newAcc);
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            await dashboardService
                .getDashboard({
                    company_id: company ?? undefined,
                    franchise_id: franchise ?? undefined,
                    grouped: grupal ?? false,
                })
                .then((response) => {
                    if (response.length > 0) {
                        setDashboardInfo(response);
                    } else {
                        setDashboardInfo([]);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        setShowSkeleton(false);
        setHistory(!history);
        setXeroView(false);
    };

    useEffect(() => {
        if (todos.steps?.step2) {
            let isDone = false;

            setTimeout(() => {
                introJs()
                    .setOptions({
                        disableInteraction: true,
                        exitOnOverlayClick: false,
                        keyboardNavigation: false,
                        nextLabel: t("dialog.continue"),
                        prevLabel: t("miscellaneous.goBack"),
                        doneLabel: t("miscellaneous.accept"),
                        steps: [
                            {
                                title: `${t("miscellaneous.step")} 2`,
                                element: "#dashboard-tutorial",
                                intro: t("miscellaneous.tutorialDash"),
                            },
                        ],
                    })
                    .oncomplete(() => {
                        isDone = true;
                        dispatch(addTutorial({ ...todos.steps, step2: false, step3: true }));
                    })
                    .onexit(() => {
                        if (!isDone) {
                            dispatch(addTutorial({ ...todos.steps, step2: false }));
                        }
                    })
                    .start();
            }, 1000);
        }
    }, [dispatch, t, todos.steps]);

    return (
        <Stack
            spacing={2}
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "center", md: "space-between" }}
        >
            {(!type || type === undefined || type === null || type === "") && (
                <LightTooltip title={t("dashboard.disabledMsg")}>
                    <span>
                        <BaseButton
                            fullWidth
                            disableElevation
                            disabled
                            endIcon={<TuneIcon sx={{ transform: "rotate(90deg)", ml: 2 }} fontSize="small" />}
                        >
                            {t("dashboard.manageAcc")}
                        </BaseButton>
                    </span>
                </LightTooltip>
            )}
            {type === "Smb" && !grupal && (
                <span>
                    <BaseButton
                        fullWidth
                        onClick={() => handleNavigation(1)}
                        disableElevation
                        id="dashboard-tutorial"
                        endIcon={<TuneIcon sx={{ transform: "rotate(90deg)", ml: 2 }} fontSize="small" />}
                    >
                        {t("dashboard.manageAcc")}
                    </BaseButton>
                </span>
            )}
            {(type === "Firm" || grupal) && (
                <span>
                    <BaseButton
                        fullWidth
                        onClick={handleClickHistory}
                        disableElevation
                        variantType={ history ? "primaryOutlined" : "primaryOutlinedHover"}
                        endIcon={history ? <CheckIcon /> : <InventoryIcon fontSize="inherit" />}
                    >
                        {t("dashboard.viewHist")}
                    </BaseButton>
                </span>
            )}
        </Stack>
    );
};

export default DashboardHeader;